import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetDeviceQueryKey,
  getListDevicesQueryKey,
} from "@workspace/api-client-react";
import { useWorkspace } from "./use-workspace";
import { getApiWebSocketOrigin } from "@/lib/api-config";
import { nextBackoffMs } from "@/player/lib/reconnect-backoff";
import { shouldForceReconnect } from "@/lib/realtime-liveness";
import type {
  ChannelNowPlaying,
  DeviceStatus,
  PlaybackState,
} from "@workspace/api-client-react";

export type DeviceLiveStatus = {
  status: DeviceStatus;
  appVersion?: string;
  lastSeenAt: string;
  /**
   * Build identity the player reported on its last heartbeat (the same object
   * the API returns as `device.clientInfo`). Undefined until a heartbeat
   * carries one; shaped by the device, so the console parses it defensively
   * (lib/client-info.ts).
   */
  clientInfo?: Record<string, unknown> | null;
  /**
   * Offline-cache readiness the player last reported (the same object the API
   * returns as `device.offlineCache`), with the server clock it landed at.
   * Undefined until a report arrives; a device that has never reported must
   * render as absent, never as "not ready".
   */
  offlineCache?: Record<string, unknown> | null;
  offlineCacheReportedAt?: string | null;
};

export type QueueSnapshotItem = {
  itemId: string;
  title: string;
  artist: string | null;
  channelType: string;
  reason: string;
  playlistName: string | null;
};

export type DeviceNowPlaying = {
  state: PlaybackState;
  channelId?: string;
  channelType?: "music" | "ads" | "announcements" | "video" | null;
  playlistId?: string;
  title?: string;
  artist?: string;
  itemId?: string;
  positionSec: number;
  durationSec?: number | null;
  volume: number;
  updatedAt: string;
  queue?: QueueSnapshotItem[];
  channels?: ChannelNowPlaying[];
};

export type CommandResult = {
  deviceId: string;
  commandId: string;
  ok: boolean;
  result?: Record<string, unknown>;
};

export type CommandAck = {
  commandId: string;
  deviceId: string;
  delivered: boolean;
};

export type SyncGroupCommandAck = {
  commandId: string;
  syncGroupId: string;
  /** Number of group devices that actually received the command. */
  delivered: number;
  deviceIds: string[];
};

export type LiveDevice = {
  id: string;
  name: string;
};

export type SignalMessage = {
  from: string;
  fromKind: "device" | "control";
  payload: unknown;
};

export type UseRealtimeOptions = {
  onCommandResult?: (result: CommandResult) => void;
  onCommandAck?: (ack: CommandAck) => void;
  onSyncGroupCommandAck?: (ack: SyncGroupCommandAck) => void;
  onGatewayError?: (error: string) => void;
  onSignal?: (signal: SignalMessage) => void;
  onBroadcastEnded?: (reason: string) => void;
  /** Live session ack / lifecycle messages (announcement, remote desktop). */
  onLiveMessage?: (data: Record<string, unknown>) => void;
};

const PING_INTERVAL_MS = 25_000;

/** Normalize the gateway's `ts` (epoch ms number) into the ISO strings the
 * consumers' `new Date(...)`/typing expect. */
function tsToIso(ts: unknown): string {
  return typeof ts === "number" && Number.isFinite(ts)
    ? new Date(ts).toISOString()
    : new Date().toISOString();
}

export function useRealtime(options: UseRealtimeOptions = {}) {
  const { activeWorkspaceId } = useWorkspace();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [liveDevices, setLiveDevices] = useState<LiveDevice[]>([]);
  const [deviceStatuses, setDeviceStatuses] = useState<Record<string, DeviceLiveStatus>>({});
  const [nowPlaying, setNowPlaying] = useState<Record<string, DeviceNowPlaying>>({});

  const wsRef = useRef<WebSocket | null>(null);
  const pingInterval = useRef<number | undefined>(undefined);
  const reconnectTimer = useRef<number | undefined>(undefined);

  // Keep the latest callback in a ref so the connection effect never restarts
  // just because the caller passed a new inline function.
  const onCommandResultRef = useRef(options.onCommandResult);
  onCommandResultRef.current = options.onCommandResult;
  const onCommandAckRef = useRef(options.onCommandAck);
  onCommandAckRef.current = options.onCommandAck;
  const onSyncGroupCommandAckRef = useRef(options.onSyncGroupCommandAck);
  onSyncGroupCommandAckRef.current = options.onSyncGroupCommandAck;
  const onGatewayErrorRef = useRef(options.onGatewayError);
  onGatewayErrorRef.current = options.onGatewayError;
  const onSignalRef = useRef(options.onSignal);
  onSignalRef.current = options.onSignal;
  const onBroadcastEndedRef = useRef(options.onBroadcastEnded);
  onBroadcastEndedRef.current = options.onBroadcastEnded;
  const onLiveMessageRef = useRef(options.onLiveMessage);
  onLiveMessageRef.current = options.onLiveMessage;

  // Returns the client-generated commandId (echoed by the gateway in the ack
  // and by the device in its command-result) so callers can correlate a
  // no-response timeout, or null when the socket is not open.
  const sendCommand = useCallback(
    (deviceId: string, command: string, args?: unknown): string | null => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return null;
      const commandId = crypto.randomUUID();
      wsRef.current.send(
        JSON.stringify({ type: "command", commandId, deviceId, command, args }),
      );
      return commandId;
    },
    [],
  );

  const sendSyncGroupCommand = useCallback(
    (
      syncGroupId: string,
      command: string,
      args?: unknown,
    ): boolean => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return false;
      wsRef.current.send(
        JSON.stringify({
          type: "sync-group-command",
          syncGroupId,
          command,
          args,
        }),
      );
      return true;
    },
    [],
  );

  // Relay a WebRTC signaling payload to a specific device (the gateway routes by
  // `to`=deviceId). Used to drive the live broadcast mesh.
  const sendSignal = useCallback((deviceId: string, payload: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "signal", to: deviceId, payload }));
    }
  }, []);

  const sendLiveMessage = useCallback((message: Record<string, unknown>): boolean => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return false;
    wsRef.current.send(JSON.stringify(message));
    return true;
  }, []);

  useEffect(() => {
    if (!activeWorkspaceId) {
      setConnected(false);
      return;
    }

    // Per-workspace connection. `disposed` guards against any async work
    // (token fetch, scheduled reconnect) firing after cleanup or a workspace switch.
    let disposed = false;
    // Previous reconnect delay; 0 = next attempt starts at the backoff floor.
    let backoff = 0;
    // Wall-clock of the last inbound frame on the CURRENT socket. The gateway
    // answers every ping with a pong, so a healthy socket produces inbound
    // traffic at least once per ping interval; prolonged silence means the
    // socket is half-open (sleep/wake, NAT drop) and must be forced closed —
    // readyState stays OPEN for minutes on its own and every command sent
    // meanwhile just vanishes.
    let lastInboundAt = Date.now();

    const clearTimers = () => {
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
        pingInterval.current = undefined;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = undefined;
      }
    };

    // Drop the previous socket without side effects: handlers detached first
    // so its onclose can never schedule a reconnect for a connection we
    // deliberately replaced (that is how parallel connect loops are born).
    const teardownSocket = () => {
      const old = wsRef.current;
      if (!old) return;
      wsRef.current = null;
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      try {
        old.close();
      } catch {
        /* already closed */
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      backoff = nextBackoffMs(backoff);
      reconnectTimer.current = window.setTimeout(connect, backoff);
    };

    // Force a fresh dial now: used by the online/visibility handlers when the
    // current socket is gone or unverifiable. Safe to call in any state.
    const redial = () => {
      if (disposed) return;
      clearTimers();
      teardownSocket();
      setConnected(false);
      backoff = 0;
      void connect();
    };

    const connect = async () => {
      if (disposed) return;
      try {
        const token = await getToken();
        if (disposed) return;
        if (!token) {
          // Clerk may briefly return null (token cache mid-refresh or auth not
          // fully loaded) — retry instead of silently staying disconnected.
          scheduleReconnect();
          return;
        }

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsBase =
          getApiWebSocketOrigin() ?? `${protocol}//${window.location.host}`;
        const wsUrl = `${wsBase}/api/realtime?workspaceId=${activeWorkspaceId}`;
        // Only one live socket per hook instance, ever.
        teardownSocket();
        // Token is sent via the WebSocket subprotocol (bearer.<token>) rather than the
        // URL query string, so it does not leak into proxy/access logs or history.
        const ws = new WebSocket(wsUrl, [`bearer.${token}`]);
        wsRef.current = ws;

        ws.onopen = () => {
          if (disposed || wsRef.current !== ws) {
            ws.close();
            return;
          }
          setConnected(true);
          backoff = 0;
          lastInboundAt = Date.now();
          ws.send(JSON.stringify({ type: "presence" }));
          pingInterval.current = window.setInterval(() => {
            if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
              return;
            }
            if (shouldForceReconnect(lastInboundAt, Date.now(), PING_INTERVAL_MS)) {
              // Two silent intervals: the connection is dead even though the
              // browser still reports OPEN. Close it so onclose reconnects.
              ws.close();
              return;
            }
            ws.send(JSON.stringify({ type: "ping" }));
          }, PING_INTERVAL_MS);
        };

        ws.onmessage = (event) => {
          lastInboundAt = Date.now();
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case "snapshot": {
                // Connect-time seed: authoritative current state for every
                // visible device. REPLACES the maps — merge would preserve
                // exactly the stale entries this frame exists to correct
                // (deltas missed while this socket was down are lost forever).
                const devices = Array.isArray(data.devices) ? data.devices : [];
                const statuses: Record<string, DeviceLiveStatus> = {};
                for (const d of devices) {
                  if (typeof d?.deviceId !== "string") continue;
                  statuses[d.deviceId] = {
                    status: d.status,
                    appVersion: d.appVersion ?? undefined,
                    lastSeenAt:
                      typeof d.lastSeenAt === "string"
                        ? d.lastSeenAt
                        : new Date(0).toISOString(),
                    clientInfo: d.client ?? null,
                    offlineCache: d.cache ?? null,
                    offlineCacheReportedAt: d.cacheReportedAt ?? null,
                  };
                }
                setDeviceStatuses(statuses);
                const rows = Array.isArray(data.nowPlaying) ? data.nowPlaying : [];
                const np: Record<string, DeviceNowPlaying> = {};
                for (const r of rows) {
                  if (typeof r?.deviceId !== "string") continue;
                  np[r.deviceId] = {
                    state: r.state,
                    channelId: r.channelId ?? undefined,
                    playlistId: r.playlistId ?? undefined,
                    title: r.title ?? undefined,
                    artist: r.artist ?? undefined,
                    itemId: r.itemId ?? undefined,
                    positionSec: typeof r.positionSec === "number" ? r.positionSec : 0,
                    durationSec: null,
                    volume: typeof r.volume === "number" ? r.volume : 100,
                    updatedAt:
                      typeof r.updatedAt === "string"
                        ? r.updatedAt
                        : new Date(0).toISOString(),
                    channels: Array.isArray(r.channels) ? r.channels : undefined,
                  };
                }
                setNowPlaying(np);
                break;
              }
              case "presence":
                setOnlineUsers(data.users || []);
                setLiveDevices(
                  Array.isArray(data.devices)
                    ? data.devices
                        .filter(
                          (d: unknown): d is { id: string; name: string } =>
                            typeof d === "object" &&
                            d !== null &&
                            typeof (d as { id?: unknown }).id === "string",
                        )
                        .map((d: { id: string; name?: string }) => ({
                          id: d.id,
                          name: typeof d.name === "string" ? d.name : d.id,
                        }))
                    : [],
                );
                break;
              case "ack":
                if (data.of === "command" && data.commandId && data.deviceId) {
                  onCommandAckRef.current?.({
                    commandId: String(data.commandId),
                    deviceId: String(data.deviceId),
                    delivered: data.delivered === true,
                  });
                } else if (data.of === "sync-group-command" && data.commandId) {
                  // Group acks carry a delivered COUNT, not a boolean; surface
                  // them so a zero-delivery fan-out is not silent success.
                  onSyncGroupCommandAckRef.current?.({
                    commandId: String(data.commandId),
                    syncGroupId: String(data.syncGroupId ?? ""),
                    delivered:
                      typeof data.delivered === "number" ? data.delivered : 0,
                    deviceIds: Array.isArray(data.deviceIds)
                      ? data.deviceIds.map(String)
                      : [],
                  });
                } else if (typeof data.of === "string") {
                  onLiveMessageRef.current?.(data as Record<string, unknown>);
                }
                break;
              case "error":
                if (typeof data.error === "string") {
                  onGatewayErrorRef.current?.(data.error);
                }
                break;
              case "device-status":
                setDeviceStatuses((prev) => ({
                  ...prev,
                  [data.deviceId]: {
                    status: data.status,
                    appVersion: data.appVersion,
                    lastSeenAt: tsToIso(data.ts),
                    // A status frame (or a heartbeat from a shell that reports
                    // no build) carries no client object. Keep what the device
                    // last told us instead of blanking it, which mirrors the
                    // server's own write rule for devices.client_info.
                    clientInfo:
                      data.client ?? prev[data.deviceId]?.clientInfo ?? null,
                    // Same rule: a status frame carries no cache report, so
                    // keep the last one rather than blanking it.
                    offlineCache: prev[data.deviceId]?.offlineCache ?? null,
                    offlineCacheReportedAt:
                      prev[data.deviceId]?.offlineCacheReportedAt ?? null,
                  },
                }));
                break;
              case "device-cache":
                // Readiness moves independently of liveness: a device can be
                // offline and still have a last-known cache state worth
                // showing, so this never touches status or lastSeenAt.
                setDeviceStatuses((prev) => {
                  const existing = prev[data.deviceId];
                  if (!existing) return prev;
                  return {
                    ...prev,
                    [data.deviceId]: {
                      ...existing,
                      offlineCache: data.cache ?? null,
                      offlineCacheReportedAt: data.reportedAt ?? null,
                    },
                  };
                });
                break;
              case "device-updated":
                // A settings/policy write landed elsewhere (another tab, the
                // fleet page, a PATCH): drop the cached device queries so
                // every console converges without a manual reload.
                if (typeof data.deviceId === "string" && activeWorkspaceId) {
                  void queryClient.invalidateQueries({
                    queryKey: getListDevicesQueryKey(activeWorkspaceId),
                  });
                  void queryClient.invalidateQueries({
                    queryKey: getGetDeviceQueryKey(activeWorkspaceId, data.deviceId),
                  });
                }
                break;
              case "now-playing":
                setNowPlaying((prev) => ({
                  ...prev,
                  [data.deviceId]: {
                    state: data.state,
                    channelId: data.channelId,
                    channelType: data.channelType,
                    playlistId: data.playlistId,
                    title: data.title,
                    artist: data.artist,
                    itemId: data.itemId,
                    positionSec: data.positionSec,
                    durationSec: data.durationSec ?? null,
                    volume: data.volume,
                    updatedAt: tsToIso(data.ts),
                    queue: Array.isArray(data.queue) ? data.queue : undefined,
                    channels: Array.isArray(data.channels)
                      ? data.channels
                      : undefined,
                  },
                }));
                break;
              case "command-result":
                onCommandResultRef.current?.({
                  deviceId: data.deviceId,
                  commandId: data.commandId,
                  ok: data.ok,
                  result: data.result,
                });
                break;
              case "signal":
                onSignalRef.current?.({
                  from: data.from,
                  fromKind: data.fromKind,
                  payload: data.payload,
                });
                break;
              case "broadcast-ended":
                onBroadcastEndedRef.current?.(
                  typeof data.reason === "string" ? data.reason : "ended",
                );
                break;
              case "announcement-started":
              case "announcement-ended":
              case "remote-desktop-started":
              case "remote-desktop-ended":
                onLiveMessageRef.current?.(data as Record<string, unknown>);
                break;
            }
          } catch (e) {
            console.error("Failed to parse realtime message", e);
          }
        };

        ws.onclose = () => {
          // A superseded socket must never drive state or reconnects.
          if (wsRef.current !== ws) return;
          wsRef.current = null;
          setConnected(false);
          if (pingInterval.current) {
            clearInterval(pingInterval.current);
            pingInterval.current = undefined;
          }
          scheduleReconnect();
        };
      } catch (e) {
        console.error("Failed to connect realtime", e);
        scheduleReconnect();
      }
    };

    // Wake/network-change recovery: a socket that survived on paper may be
    // half-open. If it is verifiably fresh, keep it; otherwise re-dial now
    // instead of waiting out a backoff timer against a dead connection.
    const handleOnline = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) redial();
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (
        !ws ||
        ws.readyState !== WebSocket.OPEN ||
        shouldForceReconnect(lastInboundAt, Date.now(), PING_INTERVAL_MS)
      ) {
        redial();
      }
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    void connect();

    return () => {
      disposed = true;
      clearTimers();
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      teardownSocket();
      // Reset live state so data never bleeds across workspace switches.
      setConnected(false);
      setOnlineUsers([]);
      setLiveDevices([]);
      setDeviceStatuses({});
      setNowPlaying({});
    };
  }, [activeWorkspaceId, getToken, queryClient]);

  return {
    connected,
    onlineUsers,
    liveDevices,
    deviceStatuses,
    nowPlaying,
    sendCommand,
    sendSyncGroupCommand,
    sendSignal,
    sendLiveMessage,
  };
}
